/**
 * WorkflowBuilder — Main entry component for the Visual Workflow Builder.
 * Composes Sidebar, BuilderCanvas, ConfigPanel, and BlueprintBrowser.
 * Handles Save/Load/Clone/New actions.
 */
import React, { useState, useCallback } from 'react';
import { apiCall } from '../../utils/apiUtils';
import { useAuth } from '../Auth/AuthContext';
import ServiceUpgradeRequired from '../Common/ServiceUpgradeRequired';
import useBuilderStore from './store/builderStore';
import Sidebar from './Sidebar';
import BuilderCanvas from './BuilderCanvas';
import ConfigPanel from './ConfigPanel';
import BlueprintBrowser from './BlueprintBrowser';
import { serializeGraph, deserializeGraph } from './utils/serialization';

const API_BASE = '/api/external/v1/workflows';

const WorkflowBuilder = () => {
    const { user } = useAuth();

    // Service guard: exempt admin, vendor, accountant; only check client role
    const isExempt = user?.role === 'admin' || user?.role === 'vendor' || user?.role === 'accountant';
    const isEnabled = isExempt || user?.api_services?.workflows?.enabled;

    const [showBrowser, setShowBrowser] = useState(false);
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState(null);

    // Store selectors
    const nodes = useBuilderStore((s) => s.nodes);
    const edges = useBuilderStore((s) => s.edges);
    const templateId = useBuilderStore((s) => s.templateId);
    const templateName = useBuilderStore((s) => s.templateName);
    const templateDescription = useBuilderStore((s) => s.templateDescription);
    const isDirty = useBuilderStore((s) => s.isDirty);
    const selectedNodeId = useBuilderStore((s) => s.selectedNodeId);
    const setTemplate = useBuilderStore((s) => s.setTemplate);
    const setTemplateName = useBuilderStore((s) => s.setTemplateName);
    const setNodes = useBuilderStore((s) => s.setNodes);
    const setEdges = useBuilderStore((s) => s.setEdges);
    const clearCanvas = useBuilderStore((s) => s.clearCanvas);
    const markClean = useBuilderStore((s) => s.markClean);

    const showToast = useCallback((message, type = 'success') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 3000);
    }, []);

    // --- Save ---
    const handleSave = async () => {
        if (!templateName.trim()) {
            showToast('Please enter a template name', 'error');
            return;
        }
        if (nodes.length === 0) {
            showToast('Add at least one node before saving', 'error');
            return;
        }

        setSaving(true);
        try {
            const serializedNodes = serializeGraph(nodes, edges);
            const payload = {
                name: templateName,
                description: templateDescription,
                nodes: serializedNodes,
                variables: [],
            };

            if (templateId) {
                // Update existing
                await apiCall(`${API_BASE}/templates/${templateId}`, {
                    method: 'PUT',
                    data: payload,
                });
                showToast('Template updated successfully');
            } else {
                // Create new
                const data = await apiCall(`${API_BASE}/templates`, {
                    method: 'POST',
                    data: payload,
                });
                setTemplate(data.template.id, templateName, templateDescription);
                showToast('Template created successfully');
            }
            markClean();
        } catch (err) {
            showToast(err.message || 'Failed to save template', 'error');
        } finally {
            setSaving(false);
        }
    };

    // --- Load (from Browser) ---
    const handleEdit = useCallback((template) => {
        const { nodes: rfNodes, edges: rfEdges } = deserializeGraph(template);
        setNodes(rfNodes);
        setEdges(rfEdges);
        setTemplate(template.id, template.name, template.description || '');
        markClean();
        setShowBrowser(false);
        showToast(`Loaded "${template.name}"`);
    }, [setNodes, setEdges, setTemplate, markClean, showToast]);

    // --- Clone callback ---
    const handleClone = useCallback((template) => {
        const { nodes: rfNodes, edges: rfEdges } = deserializeGraph(template);
        setNodes(rfNodes);
        setEdges(rfEdges);
        setTemplate(template.id, template.name, template.description || '');
        markClean();
        setShowBrowser(false);
        showToast(`Cloned "${template.name}" — it's now yours to customize`);
    }, [setNodes, setEdges, setTemplate, markClean, showToast]);

    // --- New ---
    const handleNew = () => {
        if (isDirty && !window.confirm('You have unsaved changes. Discard them?')) return;
        clearCanvas();
    };

    if (!isEnabled) {
        return (
            <ServiceUpgradeRequired
                serviceName="Workflows"
                serviceId="workflows"
                description="Build visual, multi-step document workflows with automated execution and tracking."
            />
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 56px)', background: '#0F172A', borderRadius: '12px', overflow: 'hidden' }}>
            {/* Toolbar */}
            <div style={{
                display: 'flex', alignItems: 'center', gap: '12px',
                padding: '10px 16px',
                background: '#1E293B',
                borderBottom: '1px solid rgba(255,255,255,0.06)',
                flexShrink: 0,
            }}>
                {/* Template Name Input */}
                <input
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    placeholder="Untitled Workflow"
                    style={{
                        background: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '8px',
                        padding: '8px 14px',
                        color: '#F1F5F9',
                        fontSize: '14px',
                        fontWeight: 600,
                        width: '280px',
                        outline: 'none',
                        transition: 'border-color 0.2s',
                    }}
                    onFocus={(e) => { e.target.style.borderColor = '#6366F1'; }}
                    onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.1)'; }}
                />

                {/* Status */}
                {templateId && (
                    <span style={{ color: '#64748B', fontSize: '11px', fontFamily: 'monospace' }}>
                        {templateId}
                    </span>
                )}
                {isDirty && (
                    <span style={{ color: '#F59E0B', fontSize: '11px', fontWeight: 600 }}>● Unsaved</span>
                )}

                <div style={{ flex: 1 }} />

                {/* Actions */}
                <ToolbarButton label="New" onClick={handleNew} color="#64748B" />
                <ToolbarButton label="Browse" onClick={() => setShowBrowser(true)} color="#8B5CF6" />
                <ToolbarButton label={saving ? 'Saving...' : 'Save'} onClick={handleSave} color="#6366F1" primary disabled={saving} />
            </div>

            {/* Main Content */}
            <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
                <Sidebar />
                <BuilderCanvas />
                {selectedNodeId && <ConfigPanel />}
            </div>

            {/* Browser Modal */}
            {showBrowser && (
                <BlueprintBrowser
                    onEdit={handleEdit}
                    onClone={handleClone}
                    onClose={() => setShowBrowser(false)}
                />
            )}

            {/* Toast */}
            {toast && (
                <div style={{
                    position: 'fixed', bottom: '24px', right: '24px', zIndex: 9999,
                    padding: '12px 20px', borderRadius: '10px',
                    background: toast.type === 'error' ? '#991B1B' : '#065F46',
                    color: 'white', fontSize: '13px', fontWeight: 600,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
                    animation: 'fadeIn 0.3s ease',
                }}>
                    {toast.message}
                </div>
            )}
        </div>
    );
};

const ToolbarButton = ({ label, onClick, color, primary, disabled }) => (
    <button
        onClick={onClick}
        disabled={disabled}
        style={{
            padding: '8px 16px',
            borderRadius: '8px',
            fontSize: '13px',
            fontWeight: 600,
            cursor: disabled ? 'not-allowed' : 'pointer',
            border: primary ? 'none' : `1px solid ${color}55`,
            background: primary ? color : 'transparent',
            color: primary ? 'white' : color,
            transition: 'all 0.2s ease',
            opacity: disabled ? 0.6 : 1,
        }}
    >
        {label}
    </button>
);

export default WorkflowBuilder;
